import Icons from "assets";

import Card, { CardBody, CardHeader } from "components/partials/card/card";
import SkeletonLoader from "containers/admin/clients/touchpoint/tab-components/details-tab/components/skeleton-loader";
import { IconButton } from "ui/icon-button";
import { Chip } from "ui/chip";

import type { LinkDTO } from "generated/schemas";

import { useTouchpointLinks } from "containers/admin/clients/touchpoint/tab-components/details-tab/hooks/use-touchpoint-links";
import { getVersionStatusTexts } from "containers/admin/clients/touchpoint/tab-components/details-tab/details-tab.helper";
import { TouchpointCardProps } from "containers/admin/clients/touchpoint/tab-components/details-tab/details-tab.types";
import { EmailWriterDetailsSectionSkeleton } from "containers/admin/clients/touchpoint/tab-components/details-tab/components/email-writer-details-section-skeleton";
import { useIsEmailWriterGenerating } from "containers/admin/clients/touchpoint/hooks/use-is-email-writer-generating";

const renderLinksCardBody = (isLoading: boolean, links: LinkDTO[]) => {
  const allowedPrefixes = ["https://", "tel:", "sms:", "mailto:", "#"];
  if (isLoading) {
    return <SkeletonLoader />;
  } else if (!links.length) {
    return <div className="text-sm text-gray-500 py-2">No links found in the HTML</div>;
  } else {
    return (
      <div className="space-y-3 pr-4 overflow-y-auto max-h-[557px] ">
        {links.map((link, index) => {
          const url = link.url ?? "";
          const text = link.text ?? "";
          const lacksHttps = !allowedPrefixes.some((prefix) => url.startsWith(prefix));
          return (
            <div key={index}>
              <div className="flex justify-between items-center gap-4">
                <div>
                  <div className="text-sm font-bold text-gray-700 mb-1">"{text}"</div>
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-aqua-600 underline break-all">
                    {url}
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  {lacksHttps && (
                    <Chip size="small" color="warning" variant="filled" label="lacks https://" />
                  )}
                  {url.includes("utm_") && (
                    <Chip size="small" color="warning" variant="filled" label="has UTM params" />
                  )}
                </div>
                <IconButton
                  aria-label="Copy Link"
                  size="small"
                  variant="text"
                  onClick={() => {
                    navigator.clipboard.writeText(url);
                  }}>
                  <Icons.ContentCopy />
                </IconButton>
              </div>
            </div>
          );
        })}
      </div>
    );
  }
};

const TouchpointLinksCard = ({ touchpoint, selectedVersion, ...rest }: TouchpointCardProps) => {
  const isEmailWriterGenerating = useIsEmailWriterGenerating();
  const { links, isLoading } = useTouchpointLinks(touchpoint.id, selectedVersion);

  if (!selectedVersion) {
    return null;
  }

  if (isEmailWriterGenerating) {
    return (
      <Card {...rest}>
        <CardHeader heading="Links" />
        <CardBody>
          <EmailWriterDetailsSectionSkeleton />
        </CardBody>
      </Card>
    );
  }

  return (
    <Card {...rest}>
      <CardHeader heading="Links" {...getVersionStatusTexts(selectedVersion)} />
      <CardBody>{renderLinksCardBody(isLoading, links)}</CardBody>
    </Card>
  );
};

export default TouchpointLinksCard;
